import { useMemo, useState, useEffect, useRef } from 'react'
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom'
import WorkersPage from './WorkersPage'
import ManagerTimeAdmin from './ManagerTimeAdmin'
import AnnouncementsCenter from './AnnouncementsCenter'
import JobsCalendarView from './JobsCalendarView'
import AdminOperationsCenter from './admin/AdminOperationsCenter'
import { canAccessAdminOps, canAccessAdminSection, getDefaultAdminSectionForRole } from './admin/adminConfig'
import { getAppBasePath, isPasswordRecoveryCallbackUrl, RESET_PASSWORD_PATH } from './auth/recoveryUrls'
import { normalizeAccessRole } from './workforceAccessModel'
import { formatRoleLabel } from './utils/roleFormatting'
// Phase 3B1: Auth hooks
import { useAuthSession } from './hooks/useAuthSession'
import { useProfile } from './hooks/useProfile'
import { useAuthForm } from './hooks/useAuthForm'
import { usePasswordReset } from './hooks/usePasswordReset'
import { authService } from './services/authService'
// Phase 3B2: Router modules
import {
  ROUTE_PATHS,
  VIEW_META,
  ACCESS_ROLE_OPTIONS,
  ENABLE_ROLE_SWITCHER
} from './router/routes.config'
import { getRouteState, getRouteForView } from './router/routeUtils'
import { useVisibleNavItems } from './router/useNavigationItems'
import { useRouteGuard } from './router/useRouteGuard'

// Constants imported from router/routes.config.js above

function AppShell() {
  const location = useLocation()
  const navigate = useNavigate()

  // Phase 3B1: Auth hooks for session, profile, and password reset
  const { session, setSession, sessionLoading, isSignedIn, isRecoverySession } = useAuthSession()
  const { accessRole: serverAccessRole, boundWorkerId, profileLoading, profileMessage } = useProfile(session)
  const {
    email: authEmail,
    password: authPassword,
    setEmail: setAuthEmail,
    setPassword: setAuthPassword,
    isSubmitting: authSubmitting,
    error: authError,
    message: authMessage,
    handleSignIn,
    clearFeedback: clearAuthFeedback,
    clear: clearAuthForm
  } = useAuthForm()
  const {
    password: resetPassword,
    passwordConfirm: resetPasswordConfirm,
    setPassword: setResetPassword,
    setPasswordConfirm: setResetPasswordConfirm,
    isRequestSubmitting: authResetSubmitting,
    isUpdateSubmitting: resetSubmitting,
    error: resetError,
    message: resetMessage,
    handleResetRequest,
    handleResetUpdate,
    clearFeedback: clearResetFeedback,
    clear: clearResetForm
  } = usePasswordReset(session, sessionLoading)

  // Test mode: Allow role switching via local state override
  const [testRoleOverride, setTestRoleOverride] = useState('')
  const accessRole = ENABLE_ROLE_SWITCHER && testRoleOverride ? testRoleOverride : serverAccessRole

  // Navigation menu state
  const [menuOpen, setMenuOpen] = useState(false)
  const menuContainerRef = useRef(null)

  // Close menu on outside click
  useEffect(() => {
    const handlePointerOutside = (event) => {
      if (menuContainerRef.current && !menuContainerRef.current.contains(event.target)) {
        setMenuOpen(false)
      }
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false)
      }
    }

    if (menuOpen) {
      document.addEventListener('mousedown', handlePointerOutside)
      document.addEventListener('touchstart', handlePointerOutside)
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('mousedown', handlePointerOutside)
      document.removeEventListener('touchstart', handlePointerOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  // Phase 3B2: Router modules for navigation and route guarding
  const visibleNav = useVisibleNavItems(accessRole)
  const fallbackView = visibleNav[0]?.key || 'home'
  const routeState = useMemo(() => getRouteState(location.pathname), [location.pathname])
  const hasRecoveryCallback = useMemo(
    () => isPasswordRecoveryCallbackUrl({ search: location.search, hash: location.hash }),
    [location.hash, location.search]
  )
  const requestedView = routeState.kind === 'view' ? routeState.viewKey : 'home'
  const activeView = visibleNav.some((item) => item.key === requestedView) ? requestedView : fallbackView
  const activeViewMeta = VIEW_META[activeView] || VIEW_META.home
  const shouldRedirectToRecovery =
    routeState.kind !== 'auth_reset' &&
    routeState.kind !== 'legacy_redirect' &&
    (isRecoverySession || hasRecoveryCallback)

  const adminOpsRequestedSection = useMemo(() => {
    if (activeView !== 'admin_ops') return getDefaultAdminSectionForRole(accessRole)

    if (routeState.kind === 'view' && routeState.viewKey === 'admin_ops' && routeState.adminSection) {
      if (canAccessAdminSection(accessRole, routeState.adminSection)) {
        return routeState.adminSection
      }
    }

    return getDefaultAdminSectionForRole(accessRole)
  }, [accessRole, activeView, routeState])

  // Phase 3B2: Route guard hook (replaces 3 useEffect blocks for routing logic)
  useRouteGuard({
    pathname: location.pathname,
    accessRole,
    visibleNav,
    sessionLoading,
    profileLoading,
    isSignedIn
  })

  useEffect(() => {
    if (!shouldRedirectToRecovery) return
    navigate(RESET_PASSWORD_PATH, { replace: true })
  }, [navigate, shouldRedirectToRecovery])

  // Phase 3B1: Auth form handlers now managed by hooks
  // handleSignIn, handleResetRequest, handleResetUpdate are now returned from hooks
  // we need to propagate session updates returned by handleSignIn

  const onSignIn = async (event) => {
    clearResetFeedback()
    const newSession = await handleSignIn(event)
    if (newSession) {
      setSession(newSession)
    }
  }

  const onResetPasswordRequest = () => {
    clearAuthFeedback()
    handleResetRequest(authEmail)
  }

  // Phase 3B1: Replaced with authService.signOut()
  const handleReturnToSignIn = async () => {
    await authService.signOut()
    setSession(null)
    clearResetForm()
    navigate(ROUTE_PATHS.home, { replace: true })
  }

  const handleSignOut = async () => {
    await authService.signOut()
    setSession(null)
    clearAuthForm()
    clearResetForm()
  }

  const openView = (view) => {
    if (view === 'admin_ops' && !canAccessAdminOps(accessRole)) return
    navigate(getRouteForView(view))
  }

  const openAdminOpsSection = (sectionKey) => {
    if (!canAccessAdminOps(accessRole)) return

    const normalizedSection = String(sectionKey || '')
      .trim()
      .toLowerCase()

    if (normalizedSection && canAccessAdminSection(accessRole, normalizedSection)) {
      navigate(getRouteForView('admin_ops', normalizedSection))
      return
    }

    navigate(getRouteForView('admin_ops'))
  }

  const renderHomeView = () => (
    <section className="gg-view-shell gg-home-dashboard" data-testid="active-view-home">
      <section className="gg-dashboard-hero">
        <div className="gg-dashboard-hero__copy">
          <p className="gg-dashboard-hero__eyebrow">Green Gurus</p>
          <h1>Operational Clarity For Every Shift</h1>
          <p>Manage workers, review time corrections, and navigate admin operations with one consistent visual system.</p>
        </div>
        <div className="gg-dashboard-hero__meta">
          <p className="gg-dashboard-hero__meta-label">Current Access</p>
          <p className="gg-dashboard-hero__meta-value">{formatRoleLabel(accessRole)}</p>
          <p className="gg-text-soft">Signed in as {session?.user?.email || 'Unknown user'}</p>
        </div>
      </section>

      <section className="gg-home-panels">
        <section className="gg-home-panel gg-home-panel--actions">
          <h2 className="gg-home-announcement-row__title">Quick Actions</h2>
          <div className="gg-quick-action-grid">
            {visibleNav.map((item) => (
              <button key={item.key} type="button" className="gg-quick-action" onClick={() => openView(item.key)}>
                <span className="gg-quick-action__title">{item.label}</span>
                <span className="gg-quick-action__desc">{(VIEW_META[item.key] || VIEW_META.home).description}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="gg-home-panel">
          <h2 className="gg-home-announcement-row__title">Workspace Snapshot</h2>
          <div className="gg-urgent-list">
            {visibleNav.map((item) => (
              <article key={item.key} className="gg-urgent-item">
                <div>
                  <p className="gg-urgent-item__title">{item.label}</p>
                  <p className="gg-urgent-item__meta">{(VIEW_META[item.key] || VIEW_META.home).title}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      </section>
    </section>
  )

  const renderResetPasswordView = () => (
    <main className="gg-access-main gg-theme">
      <section className="gg-access-gateway gg-access-gateway--split">
        <div className="gg-access-gateway__split">
          <aside className="gg-access-gateway__brand">
            <span className="gg-access-gateway__brand-mark">GG</span>
            <p className="gg-access-gateway__brand-kicker">Green Gurus</p>
            <h1 className="gg-access-gateway__brand-title">Set your new password</h1>
            <p className="gg-access-gateway__brand-description">
              Use the recovery link from your email to complete this password update.
            </p>
            <ul className="gg-access-gateway__brand-list">
              <li>Secure account recovery</li>
              <li>One-time recovery session</li>
              <li>Return to normal sign in after update</li>
            </ul>
          </aside>

          <section className="gg-access-gateway__panel gg-access-gateway__panel--auth" data-testid="reset-password-screen">
            <div className="gg-access-gateway__intro">
              <p className="gg-access-gateway__eyebrow">Green Gurus</p>
              <h2 className="gg-access-gateway__title">Reset Password</h2>
              <p className="gg-access-gateway__description">
                {sessionLoading ? 'Checking your recovery session...' : 'Enter a new password to finish recovery.'}
              </p>
            </div>

            <form className="gg-auth-form" onSubmit={(e) => handleResetUpdate(e)}>
              <label className="gg-auth-form__field">
                <span>New Password</span>
                <input
                  className="gg-input"
                  type="password"
                  value={resetPassword}
                  onChange={(event) => setResetPassword(event.target.value)}
                  autoComplete="new-password"
                  placeholder="Enter new password"
                />
              </label>
              <label className="gg-auth-form__field">
                <span>Confirm New Password</span>
                <input
                  className="gg-input"
                  type="password"
                  value={resetPasswordConfirm}
                  onChange={(event) => setResetPasswordConfirm(event.target.value)}
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                />
              </label>
              <div className="gg-auth-form__actions">
                <button
                  type="submit"
                  className="gg-button gg-button--primary gg-auth-form__submit"
                  disabled={resetSubmitting || sessionLoading}
                >
                  {resetSubmitting ? 'Updating password...' : 'Update Password'}
                </button>
              </div>
            </form>

            {!sessionLoading && !session ? (
              <p className="gg-inline-alert gg-inline-alert--error">
                Recovery session not found. Open the newest reset link from your email.
              </p>
            ) : null}
            {resetError ? <p className="gg-inline-alert gg-inline-alert--error">{resetError}</p> : null}
            {resetMessage ? <p className="gg-inline-alert gg-inline-alert--success">{resetMessage}</p> : null}
            <div className="gg-auth-form__secondary">
              <button type="button" className="gg-button gg-button--secondary gg-auth-form__secondary-action" onClick={handleReturnToSignIn}>
                Return to Sign In
              </button>
            </div>
          </section>
        </div>
      </section>
    </main>
  )

  const renderActiveView = () => {
    if (activeView === 'workers') {
      return <WorkersPage />
    }

    if (activeView === 'time_admin') {
      return (
        <section className="gg-view-shell gg-workspace" data-testid="active-view-time_admin">
          <ManagerTimeAdmin
            userRole={accessRole}
            userWorkerId={boundWorkerId}
            isAdminPreview={false}
          />
        </section>
      )
    }

    if (activeView === 'announcements') {
      return (
        <section className="gg-view-shell gg-workspace" data-testid="active-view-announcements">
          <AnnouncementsCenter />
        </section>
      )
    }

    if (activeView === 'calendar') {
      return (
        <section className="gg-view-shell gg-workspace" data-testid="active-view-calendar">
          <JobsCalendarView />
        </section>
      )
    }

    if (activeView === 'admin_ops') {
      return (
        <section className="gg-view-shell gg-workspace" data-testid="active-view-admin_ops">
          <AdminOperationsCenter
            accessRole={accessRole}
            workspaceRole={accessRole}
            userWorkerId={boundWorkerId}
            requestedSection={adminOpsRequestedSection}
            onOpenWorkers={() => navigate(getRouteForView('workers'))}
            onOpenJobs={() => openAdminOpsSection('workforce')}
            onOpenSites={() => openAdminOpsSection('workforce')}
            onOpenManagerTime={() => navigate(getRouteForView('time_admin'))}
            onOpenIssueCenter={() => openAdminOpsSection('issues')}
            onOpenAnnouncements={() => navigate(getRouteForView('announcements'))}
            onOpenAccessTools={() => openAdminOpsSection('access')}
          />
        </section>
      )
    }

    return renderHomeView()
  }

  if (routeState.kind === 'auth_reset') {
    return renderResetPasswordView()
  }

  if (shouldRedirectToRecovery) {
    return (
      <main className="gg-access-main gg-theme">
        <section className="gg-access-gateway">
          <section className="gg-access-gateway__panel gg-access-gateway__panel--auth">
            <p className="gg-access-gateway__description">Redirecting to password reset...</p>
          </section>
        </section>
      </main>
    )
  }

  if (sessionLoading || (session && profileLoading)) {
    return (
      <main className="gg-access-main gg-theme">
        <section className="gg-access-gateway">
          <section className="gg-access-gateway__panel gg-access-gateway__panel--auth">
            <p className="gg-access-gateway__description">
              {sessionLoading ? 'Checking your account session...' : 'Loading access profile...'}
            </p>
          </section>
        </section>
      </main>
    )
  }

  if (!session) {
    return (
      <main className="gg-access-main gg-theme">
        <section className="gg-access-gateway gg-access-gateway--split">
          <div className="gg-access-gateway__split">
            <aside className="gg-access-gateway__brand">
              <span className="gg-access-gateway__brand-mark">GG</span>
              <p className="gg-access-gateway__brand-kicker">Green Gurus</p>
              <h1 className="gg-access-gateway__brand-title">Sign in to your workspace</h1>
              <p className="gg-access-gateway__brand-description">
                Access scheduling, hours, and workforce tools from one secure portal.
              </p>
              <ul className="gg-access-gateway__brand-list">
                <li>Workforce operations</li>
                <li>Hours and pay tracking</li>
                <li>Role-based access controls</li>
              </ul>
            </aside>

            <section className="gg-access-gateway__panel gg-access-gateway__panel--auth" data-testid="login-screen">
              <div className="gg-access-gateway__intro">
                <p className="gg-access-gateway__eyebrow">Green Gurus</p>
                <h2 className="gg-access-gateway__title">Welcome Back</h2>
                <p className="gg-access-gateway__description">Sign in with your email and password.</p>
              </div>

              <form className="gg-auth-form" onSubmit={onSignIn}>
                <label className="gg-auth-form__field">
                  <span>Email</span>
                  <input
                    className="gg-input"
                    type="email"
                    value={authEmail}
                    onChange={(event) => setAuthEmail(event.target.value)}
                    autoComplete="email"
                    placeholder="you@greengurus.com"
                  />
                </label>
                <label className="gg-auth-form__field">
                  <span>Password</span>
                  <input
                    className="gg-input"
                    type="password"
                    value={authPassword}
                    onChange={(event) => setAuthPassword(event.target.value)}
                    autoComplete="current-password"
                    placeholder="Enter password"
                  />
                </label>
                <div className="gg-auth-form__actions">
                  <button type="submit" className="gg-button gg-button--primary gg-auth-form__submit" disabled={authSubmitting}>
                    {authSubmitting ? 'Signing in...' : 'Sign In'}
                  </button>
                </div>
                <div className="gg-auth-form__secondary">
                  <p className="gg-auth-form__secondary-copy">Need to reset your password?</p>
                  <button
                    type="button"
                    className="gg-button gg-button--secondary gg-auth-form__secondary-action"
                    onClick={onResetPasswordRequest}
                    disabled={authResetSubmitting || authSubmitting}
                  >
                    {authResetSubmitting ? 'Sending reset link...' : 'Reset Password'}
                  </button>
                </div>
              </form>

              {authError ? <p className="gg-inline-alert gg-inline-alert--error">{authError}</p> : null}
              {resetError ? <p className="gg-inline-alert gg-inline-alert--error">{resetError}</p> : null}
              {authMessage ? <p className="gg-inline-alert gg-inline-alert--success">{authMessage}</p> : null}
              {resetMessage ? <p className="gg-inline-alert gg-inline-alert--success">{resetMessage}</p> : null}
            </section>
          </div>
        </section>
      </main>
    )
  }

  return (
    <div className="gg-app-layout gg-theme" data-testid="authenticated-shell">
      <header className="gg-top-nav">
        <div className="gg-top-nav__inner">
          <div className="gg-brand-block">
            <p className="gg-brand-block__kicker">Green Gurus</p>
            <p className="gg-brand-block__title">Workforce Operations</p>
          </div>
          <div className="gg-top-nav__nav-group" ref={menuContainerRef}>
            <nav className="gg-nav-links" aria-label="Primary">
              {visibleNav.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`gg-nav-link ${activeView === item.key ? 'gg-nav-link--active' : ''}`}
                  onClick={() => openView(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <button
              type="button"
              className="gg-menu-toggle"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle navigation menu"
              aria-controls="gg-primary-nav-mobile"
              aria-expanded={menuOpen}
            >
              <span className="gg-menu-toggle__line"></span>
              <span className="gg-menu-toggle__line"></span>
              <span className="gg-menu-toggle__line"></span>
            </button>

            {menuOpen ? (
              <nav id="gg-primary-nav-mobile" className="gg-nav-menu" aria-label="Primary mobile">
                {visibleNav.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`gg-nav-menu__item ${activeView === item.key ? 'gg-nav-menu__item--active' : ''}`}
                    onClick={() => {
                      openView(item.key)
                      setMenuOpen(false)
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
            ) : null}
          </div>
        </div>

        <div className="gg-top-nav__sub">
          <div className="gg-top-nav__sub-copy">
            <p className="gg-top-nav__workspace">{formatRoleLabel(accessRole)} Workspace</p>
            <p className="gg-top-nav__sub-title">{activeViewMeta.title}</p>
            <p className="gg-top-nav__sub-description">{activeViewMeta.description}</p>
            {profileMessage ? <p className="gg-inline-alert gg-inline-alert--warning">{profileMessage}</p> : null}
          </div>
          <div className="gg-top-nav__actions">
            {ENABLE_ROLE_SWITCHER ? (
              <label>
                Access Role
                <select
                  className="gg-filter-control"
                  value={testRoleOverride || serverAccessRole}
                  onChange={(event) => setTestRoleOverride(normalizeAccessRole(event.target.value))}
                  style={{ marginLeft: 8 }}
                >
                  {ACCESS_ROLE_OPTIONS.map((role) => (
                    <option key={role} value={role}>
                      {formatRoleLabel(role)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <button type="button" className="gg-button gg-button--ghost gg-top-nav__switch" onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="gg-main-content">{renderActiveView()}</main>
    </div>
  )
}

function App() {
  const appBasePath = getAppBasePath()
  const shouldUseBasename =
    appBasePath &&
    (typeof window === 'undefined' ||
      window.location.pathname === appBasePath ||
      window.location.pathname.startsWith(`${appBasePath}/`))

  return (
    <BrowserRouter
      basename={shouldUseBasename ? appBasePath : undefined}
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <AppShell />
    </BrowserRouter>
  )
}

export default App
